import { useEffect } from "react";

import { useMedplum } from "@medplum/react";

import { fetchBirthDateTimeObservation } from "@/encounters/services/observations";
import { useAppDispatch } from "@/store";
import { clearBirthState, setBirthDateTime, setBirthObservations } from "@/store/slices/birth";

/**
 * Loads the patient's birth date-time observation into the birth slice.
 *
 * Clears the slice each time `clientId` changes, so a patient without a birth observation never
 * shows the previous patient's birth time. A response that arrives after `clientId` has changed
 * is ignored.
 *
 * @param clientId - The patient whose birth observation to load. While undefined, nothing loads
 *   and the slice keeps its current value for whichever encounter owns it.
 */
export function useLoadBirthDateTime(clientId: string | undefined): void {
  const dispatch = useAppDispatch();
  const medplum = useMedplum();

  useEffect(() => {
    if (!clientId) return;

    let isActive = true;
    dispatch(clearBirthState());

    (async () => {
      const birthingObservation = await fetchBirthDateTimeObservation(medplum, clientId);
      if (isActive && birthingObservation) {
        dispatch(setBirthObservations([birthingObservation]));
        dispatch(setBirthDateTime(birthingObservation.valueDateTime));
      }
    })();

    return () => {
      isActive = false;
    };
  }, [clientId, dispatch, medplum]);
}
